// src/helpers/pdfTarjetaCliente.js
import notify from "../componentes/common/notify";
import JsBarcode from "jsbarcode";
import { jsPDF } from "jspdf";

/**
 * Genera un PDF de 2 páginas tamaño tarjeta (CR80 ~ 85.6 x 54 mm):
 *  - Página 1: Tarjeta de Cliente con código C:<clienteId>
 *  - Página 2: Lista de deudas con códigos D:<deudaId>
 *
 * @param {Object} cliente
 * @param {Object|null} deudaPrincipal
 * @param {Array<Object>} deudasCliente  // arreglo de deudas del cliente
 */
export const generarTarjetaClientePdf = (
  cliente,
  deudaPrincipal,
  deudasCliente = []
) => {
  if (!cliente || !cliente.id) {
    notify.error("Faltan datos del cliente para generar la tarjeta.");
    return;
  }

  // Tamaño tarjeta CR80 aprox: 85.6 x 54 mm
  const width = 85.6;
  const height = 54;

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [height, width], // [alto, ancho]
  });

  const marginX = 5;
  const marginY = 5;

  const drawFront = () => {
    doc.setDrawColor(180, 180, 180);
    doc.roundedRect(
      marginX,
      marginY,
      width - marginX * 2,
      height - marginY * 2,
      3,
      3
    );

    // Encabezado
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("GRUPO INMOBILIARIO K&E", width / 2, marginY + 6, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Tarjeta de Cliente", width / 2, marginY + 10, {
      align: "center",
    });

    // Datos texto
    const textStartY = marginY + 16;
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text("Cliente:", marginX + 2, textStartY);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${cliente.nombre || ""} ${cliente.apellidos || ""}`,
      marginX + 18,
      textStartY
    );

    if (deudaPrincipal) {
      doc.setFont("helvetica", "bold");
      doc.text("Deuda principal:", marginX + 2, textStartY + 4);
      doc.setFont("helvetica", "normal");
      doc.text(
        `${deudaPrincipal.folio || deudaPrincipal.id}`,
        marginX + 24,
        textStartY + 4
      );
    }

    // ===== código cliente =====
    const codigoCliente = `C:${cliente.id}`;

    const generarCodigo = (valor, options = {}) => {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, valor, {
        format: "CODE128",
        displayValue: false,
        margin: 0,
        width: options.width || 1.0,
        height: options.height || 10, // barras bajas
      });
      return canvas.toDataURL("image/png");
    };

    if (codigoCliente) {
      const imgCliente = generarCodigo(codigoCliente, { height: 10 });

      const barcodeWidthMm = width - marginX * 2 - 4;
      const barcodeHeightMm = 4; // MUY compacto

      const yBarcode = textStartY + 10;

      doc.setFontSize(6);
      doc.text("ID Cliente", width / 2, yBarcode - 1, { align: "center" });

      doc.addImage(
        imgCliente,
        "PNG",
        marginX + 2,
        yBarcode,
        barcodeWidthMm,
        barcodeHeightMm
      );
    }

    // Nota
    doc.setFontSize(5.5);
    doc.text(
      "Escanea este código para localizar rápidamente al cliente.",
      width / 2,
      height - marginY - 2,
      { align: "center" }
    );
  };

  const drawBack = () => {
  // Marco
  doc.setDrawColor(180, 180, 180);
  doc.roundedRect(
    marginX,
    marginY,
    width - marginX * 2,
    height - marginY * 2,
    3,
    3
  );

  // Título
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Deudas del Cliente", width / 2, marginY + 7, {
    align: "center",
  });

  // Nombre
  doc.setFontSize(6);
  doc.setFont("helvetica", "normal");
  doc.text(
    `${cliente.nombre || ""} ${cliente.apellidos || ""}`,
    width / 2,
    marginY + 12,
    { align: "center" }
  );

  // Hasta 4 deudas máximo
  const maxDeudas = 4;
  const deudas = (deudasCliente || []).slice(0, maxDeudas);

  if (!deudas.length) {
    doc.setFontSize(6);
    doc.text("Sin deudas registradas.", width / 2, height / 2, {
      align: "center",
    });
    return;
  }

  // Generador de código
  const generarCodigo = (valor) => {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, valor, {
      format: "CODE128",
      displayValue: false,
      margin: 0,
      width: 0.6,
      height: 14, // altura antes de rotar
    });
    return canvas.toDataURL("image/png");
  };

  // Área interna (dejamos margen para título y nota)
  const innerTop = marginY + 16;
  const innerBottom = height - marginY - 8;
  const innerHeight = innerBottom - innerTop;

  const innerLeft = marginX + 6;                // margen lateral extra
  const innerRight = width - marginX - 6;
  const innerWidth = innerRight - innerLeft;

  const numCols = deudas.length;
  const colWidth = innerWidth / numCols;

  const barcodeHeightMm = innerHeight * 0.6;    // 60% de alto para barras
  const textBlockHeight = innerHeight * 0.4;    // 40% para texto
  void textBlockHeight;

  deudas.forEach((deuda, i) => {
    const colLeft = innerLeft + i * colWidth;
    const colCenterX = colLeft + colWidth / 2;

    const folio = deuda.folio || deuda.id;
    const conceptoFull = (deuda.concepto || deuda.tipo || "")
      .toString()
      .trim();
    const concepto =
      conceptoFull.length > 22 ? conceptoFull.slice(0, 22) + "…" : conceptoFull;

    // CÓDIGO vertical centrado en parte superior de la columna
    const codigoDeuda = `D:${deuda.id}`;
    const imgDeuda = generarCodigo(codigoDeuda);

    const barcodeWidthMm = colWidth * 0.4;          // 40% del ancho de columna
    const barcodeX = colCenterX - barcodeWidthMm / 2;
    const barcodeY = innerTop + 2;                  // un poco bajo el encabezado

    doc.addImage(
      imgDeuda,
      "PNG",
      barcodeX,
      barcodeY,
      barcodeWidthMm,
      barcodeHeightMm,
      undefined,
      undefined,
      90 // rotado 90°: barras verticales
    );

    // TEXTO bajo el código
    const textBaseY = barcodeY + barcodeHeightMm + 2;
    const maxTextY = innerTop + innerHeight;        // no pasar nota

    let y = Math.min(textBaseY, maxTextY - 7);      // por seguridad

    doc.setFont("helvetica", "bold");
    doc.setFontSize(5);
    doc.text(`${i + 1}. ${folio}`, colCenterX, y, { align: "center" });

    if (concepto) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(4.3);
      y += 3;
      // Se parte automáticamente en dos si es necesario
      const l1 = concepto.slice(0, 14);
      const l2 = concepto.length > 14 ? concepto.slice(14, 28) : "";
      doc.text(l1, colCenterX, y, { align: "center" });
      if (l2) doc.text(l2, colCenterX, y + 3, { align: "center" });
    }
  });

  // Nota
  doc.setFontSize(5);
  doc.text(
    "Escanee una deuda para ir directo a su detalle.",
    width / 2,
    height - marginY - 2,
    { align: "center" }
  );
};
  // Página 1 (frente)
  drawFront();

  // Página 2 (reverso)
  doc.addPage([height, width], "landscape");
  drawBack();

  const nombreArchivo = `tarjeta_cliente_${cliente.nombre || ""}_${
    cliente.apellidos || ""
  }`.replace(/\s+/g, "_");

  doc.save(`${nombreArchivo}.pdf`);
};
